import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useSearchParams } from 'react-router-dom';
import { resetPasswordWithToken } from '../services/api';
import AuthShell from '../components/auth/AuthShell';
import AuthFormLayout from '../components/auth/AuthFormLayout';
import AuthErrorBanner from '../components/auth/AuthErrorBanner';

const resetPasswordSchema = z
  .object({
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .max(128, 'Password is too long'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationBanner, setValidationBanner] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);

  useEffect(() => {
    if (!token || token.trim() === '') {
      setTokenError(
        'Invalid or missing reset link. Please request a new password reset.'
      );
    }
  }, [token]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
  });

  const dismissBanner = useCallback(() => {
    setError(null);
    setValidationBanner(null);
  }, []);

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!token) return;
    setIsLoading(true);
    setError(null);
    setValidationBanner(null);
    try {
      await resetPasswordWithToken(token, data.password, data.confirmPassword);
      setSuccess(true);
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          'Failed to reset password. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const onInvalid = () => {
    setError(null);
    setValidationBanner('Please fix the errors below and try again.');
  };

  const bannerMessage = error ?? validationBanner;

  return (
    <AuthShell showBack backTo="/login" backLabel="Back to login">
      {bannerMessage && (
        <AuthErrorBanner message={bannerMessage} onDismiss={dismissBanner} />
      )}
      <AuthFormLayout
        heading="Reset password"
        subtext="Enter your new password below"
      >
        {tokenError ? (
          <div className="space-y-4">
            <p className="auth-error text-center text-sm leading-relaxed">
              {tokenError}
            </p>
            <Link to="/forgot-password" className="auth-btn-primary block text-center">
              Request new reset link
            </Link>
            <div className="pt-3 text-center">
              <Link to="/login" className="auth-link text-sm">
                Back to login
              </Link>
            </div>
          </div>
        ) : success ? (
          <div className="space-y-6">
            <div className="auth-success" role="status">
              Password has been reset successfully. You can now log in.
            </div>
            <div className="text-center">
              <Link to="/login" className="auth-btn-primary block text-center">
                Go to login
              </Link>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="space-y-4">
            <div>
              <input
                id="password"
                type="password"
                className="auth-input"
                placeholder="New password"
                autoComplete="new-password"
                {...register('password')}
              />
              {errors.password && (
                <p className="auth-error">{errors.password.message}</p>
              )}
            </div>

            <div>
              <input
                id="confirmPassword"
                type="password"
                className="auth-input"
                placeholder="Confirm new password"
                autoComplete="new-password"
                {...register('confirmPassword')}
              />
              {errors.confirmPassword && (
                <p className="auth-error">{errors.confirmPassword.message}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="auth-btn-primary"
              aria-busy={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="auth-btn-spinner" aria-hidden="true" />
                  Resetting…
                </span>
              ) : (
                'Reset password'
              )}
            </button>

            <div className="pt-3 text-center">
              <Link to="/login" className="auth-link text-sm">
                Back to login
              </Link>
            </div>
          </form>
        )}
      </AuthFormLayout>
    </AuthShell>
  );
};

export default ResetPasswordPage;
